import type { ComponentType, SVGProps } from 'react';

import { Mars, Trees, Venus } from 'lucide-react';

import {
  CloseCircleIcon,
  CupMusicIcon,
  HomeIcon,
  MicLessonIcon,
  MusicNotesIcon,
  UserHandUpIcon,
  VolumeCrossIcon,
} from '../../../components';

import type { SurveyStepId } from '../survey';

/**
 * 설문 선택지 왼쪽의 아이콘 타일 (시안 §2 step2·4·7).
 *
 * 세 단계만 아이콘을 갖는다. 워너비 스타일·목적·컨디션은 시안에서도 글자만이라
 * 여기 없는 단계는 타일 없이 렌더된다 — `undefined`가 곧 "이 단계는 글자만"이다.
 *
 * 색은 아이콘마다 다르다. 내보낸 시안 SVG는 색이 그림 안에 박혀 있어 그대로 뜨고
 * (`docs/figma/assets/README.md`), 시안에 없어 lucide로 대신한 셋만 여기서 색을 준다.
 */

interface OptionIcon {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** 시안 SVG가 제 색을 갖고 있으면 생략한다. */
  className?: string;
}

/**
 * **성별 기호는 시안 SVG가 아니라 lucide다.** 시안의 그 두 자리는 내보낸 아이콘 목록에
 * 없고(`tools/figma-icons.json`), 색은 시안 렌더에서 뽑았다 — 분홍 `#FB233B`, 파랑 `#007AFF`.
 * 레슨 마이크는 시안 `758:20618`에서 내보낸 보라 그림(`mic-lesson.svg`)이다.
 *
 * **시안의 성별 아이콘은 뒤바뀌어 있다** (`docs/figma/SCREENS.md` step2: 여성 카드에 ♂,
 * 남성 카드에 ♀). 그대로 옮기면 화면에 그대로 틀린 채 나가므로 바로잡아 붙인다.
 */
const OPTION_ICONS: Partial<Record<SurveyStepId, Record<string, OptionIcon>>> =
  {
    gender: {
      female: { Icon: Venus, className: 'text-[#FB233B]' },
      male: { Icon: Mars, className: 'text-[#007AFF]' },
      // 원본 잉크(`#010C32`)를 되돌려 준다 — 원은 이 색의 20% 레이어라 회색으로 뜬다.
      none: { Icon: CloseCircleIcon, className: 'text-[#010C32]' },
    },
    experience: {
      first: { Icon: UserHandUpIcon },
      hobby: { Icon: MusicNotesIcon },
      lesson: { Icon: MicLessonIcon, className: 'h-7 w-6' },
      pro: { Icon: CupMusicIcon },
    },
    environment: {
      quiet: { Icon: HomeIcon },
      noisy: { Icon: VolumeCrossIcon },
      // 야외는 시안에 없던 선택지라 아이콘도 색도 시안에 근거가 없다 — 나머지 둘이
      // 타일을 갖는 줄에서 혼자 비면 줄이 어긋나므로 lucide로 임시로 채웠다.
      // 시안이 이 자리를 정하면 그 값으로 바꾼다.
      outdoor: { Icon: Trees, className: 'text-[#34C759]' },
    },
  };

interface SurveyOptionIconProps {
  stepId: SurveyStepId;
  value: string;
}

/** 이 선택지에 시안이 아이콘을 두었는지. 타일 자리를 만들지 말지를 호출부가 정한다. */
export const hasSurveyOptionIcon = (
  stepId: SurveyStepId,
  value: string,
): boolean => OPTION_ICONS[stepId]?.[value] !== undefined;

/**
 * 타일 한 장. 그림은 글자 라벨 옆에 있고 같은 뜻을 되풀이하므로 스크린리더에서 숨긴다.
 * 크기는 시안 렌더에서 잰 값이다 — 타일 56px(750px 목업에서 112px), 그림 32px.
 */
export const SurveyOptionIcon = ({ stepId, value }: SurveyOptionIconProps) => {
  const icon = OPTION_ICONS[stepId]?.[value];
  if (!icon) {
    return null;
  }
  const { Icon, className } = icon;
  return (
    <span
      aria-hidden="true"
      data-testid="survey-option-icon"
      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[var(--bk-50)]"
    >
      <Icon className={className} width="2rem" height="2rem" />
    </span>
  );
};
